import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent, Grid, CardActionArea } from '@mui/material';

interface Tool {
    name: string;
    type: string;
    description: string;
}

interface Agent {
    _id: string;
    name: string;
    role: string;
    description: string;
    prompt_template: string;
    tools: Tool[];
    created_at: string;
    updated_at: string;
}

export const AgentesPage: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
    const [agentValues, setAgentValues] = useState({
        name: '',
        role: '',
        description: '',
        prompt_template: '',
        tools: [{ name: '', type: '', description: '' }],
    });

    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            const response = await fetch('http://localhost:5000/api/agents');
            if (!response.ok) {
                throw new Error('Error al obtener los agentes');
            }
            const data = await response.json();
            setAgents(data);
        } catch (error) {
            console.error('Error al obtener los agentes:', error);
        }
    };

    const handleAddAgent = async () => {
        try {
            const response = await fetch('http://localhost:5000/api/agents', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(agentValues),
            });

            if (!response.ok) {
                throw new Error('Error al guardar los datos del agente');
            }

            const newAgent = await response.json();
            setAgents(prevAgents => [...prevAgents, newAgent]);
            setOpen(false);
            setAgentValues({
                name: '',
                role: '',
                description: '',
                prompt_template: '',
                tools: [{ name: '', type: '', description: '' }],
            });
            fetchAgents(); // Actualizar la lista de agentes

        } catch (error) {
            console.error('Error al guardar los datos del agente:', error);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setAgentValues(prevValues => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleToolChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setAgentValues(prevValues => {
            const tools = [...prevValues.tools];
            tools[index] = { ...tools[index], [name]: value };
            return { ...prevValues, tools };
        });
    };

    const handleAddTool = () => {
        setAgentValues(prevValues => ({
            ...prevValues,
            tools: [...prevValues.tools, { name: '', type: '', description: '' }],
        }));
    };

    const handleAgentClick = (agent: Agent) => {
        setSelectedAgent(agent);
    };

    return (
        <Container maxWidth="lg">
            <Typography variant="h4" component="h1" gutterBottom>
                Gestión de Agentes
            </Typography>

            <Button variant="contained" onClick={() => setOpen(true)} sx={{ mt: 2, mb: 4 }}>
                Crear Nuevo Agente
            </Button>

            <Grid container spacing={3}>
                {agents.map((agent) => (
                    <Grid item key={agent._id} xs={12} sm={6} md={4}>
                        <Card sx={{ height: '100%' }}>
                            <CardActionArea onClick={() => handleAgentClick(agent)}>
                                <CardContent>
                                    <Typography variant="h5" component="div">
                                        {agent.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Rol: {agent.role}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Descripción: {agent.description.substring(0, 100)}...
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Modal para crear agente */}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Crear Nuevo Agente</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        name="name"
                        label="Nombre del Agente"
                        fullWidth
                        value={agentValues.name}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="dense"
                        name="role"
                        label="Rol del Agente"
                        fullWidth
                        value={agentValues.role}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="dense"
                        name="description"
                        label="Descripción"
                        fullWidth
                        value={agentValues.description}
                        onChange={handleChange}
                    />
                    <TextField
                        margin="dense"
                        name="prompt_template"
                        label="Plantilla de Prompt"
                        fullWidth
                        value={agentValues.prompt_template}
                        onChange={handleChange}
                    />
                    {agentValues.tools.map((tool, index) => (
                        <div key={index}>
                            <Typography variant="h6" sx={{ mt: 2 }}>Herramienta {index + 1}</Typography>
                            <TextField
                                margin="dense"
                                name="name"
                                label="Nombre de la Herramienta"
                                fullWidth
                                value={tool.name}
                                onChange={(e) => handleToolChange(index, e)}
                            />
                            <TextField
                                margin="dense"
                                name="type"
                                label="Tipo de la Herramienta"
                                fullWidth
                                value={tool.type}
                                onChange={(e) => handleToolChange(index, e)}
                            />
                            <TextField
                                margin="dense"
                                name="description"
                                label="Descripción de la Herramienta"
                                fullWidth
                                value={tool.description}
                                onChange={(e) => handleToolChange(index, e)}
                            />
                        </div>
                    ))}
                    <Button onClick={handleAddTool} sx={{ mt: 2 }}>
                        Agregar Herramienta
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleAddAgent}>Guardar</Button>
                </DialogActions>
            </Dialog>

            {/* Modal para mostrar detalles del agente */}
            <Dialog open={!!selectedAgent} onClose={() => setSelectedAgent(null)} maxWidth="md" fullWidth>
                {selectedAgent && (
                    <>
                        <DialogTitle>{selectedAgent.name}</DialogTitle>
                        <DialogContent>
                            <Typography variant="h6">Rol</Typography>
                            <Typography paragraph>{selectedAgent.role}</Typography>
                            
                            <Typography variant="h6">Descripción</Typography>
                            <Typography paragraph>{selectedAgent.description}</Typography>
                            
                            <Typography variant="h6">Plantilla de Prompt</Typography>
                            <Typography paragraph>{selectedAgent.prompt_template}</Typography>
                            
                            <Typography variant="h6">Herramientas</Typography>
                            {selectedAgent.tools.map((tool, index) => (
                                <div key={index}>
                                    <Typography variant="subtitle1">{tool.name}</Typography>
                                    <Typography>Tipo: {tool.type}</Typography>
                                    <Typography paragraph>Descripción: {tool.description}</Typography>
                                </div>
                            ))}
                            
                            <Typography variant="body2">ID: {selectedAgent._id}</Typography>
                            <Typography variant="body2">Creado: {new Date(selectedAgent.created_at).toLocaleString()}</Typography>
                            <Typography variant="body2">Actualizado: {new Date(selectedAgent.updated_at).toLocaleString()}</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSelectedAgent(null)}>Cerrar</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Container>
    );
};